import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { useApiMutation } from '../../../shared/useApi.js';

const KEY = ['purchases'];

// استجابات «الفاتورة الواحدة» كائنات فيها `items`؛ مُعترض shared/api.js صار
// يفرّق بين الصفحة والمستند (الصفحة بلا `_id`) فلا يلمسها.
async function invoiceRequest(method, url, body) {
  const res = await api.request({ method, url, data: body });
  return res.data;
}

/** معرّف الفاتورة من أي استجابة (فاتورة مباشرة أو { invoice }). */
export function invoiceIdOf(doc) {
  if (!doc) return null;
  const inv = doc.invoice || doc;
  return inv._id || inv.id || null;
}

export function usePurchases(filters = {}) {
  // «عرض الكل»: لا يُرسَل ترقيم إطلاقاً — الخادم يرشّح كل الفواتير
  // ويعيدها في قائمة واحدة (بسقف أمان يعيده في `cap`/`truncated`).
  const params = filters.all
    ? { all: true }
    : { page: filters.page || 1, limit: filters.limit || 20 };
  if (filters.q) params.q = filters.q;
  if (filters.status) params.status = filters.status;
  if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
  if (filters.supplierId) params.supplierId = filters.supplierId;
  if (filters.warehouseId) params.warehouseId = filters.warehouseId;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;

  return useQuery({
    queryKey: [...KEY, 'list', params],
    queryFn: async () => {
      const { data } = await api.get('/purchases', { params });
      return {
        items: data?.items || [],
        total: data?.total || 0,
        page: data?.page || 1,
        pages: data?.pages || 1,
        limit: data?.limit || 20,
        mode: data?.mode || 'page',
        truncated: !!data?.truncated,
        notice: data?.notice || ''
      };
    },
    keepPreviousData: true,
    staleTime: 15_000
  });
}

export function usePurchase(id) {
  // معرّف غير صالح (مثل /purchase/undefined بعد تنقّل خاطئ) لا يُطلب من الخادم.
  const valid = !!id && id !== 'undefined' && id !== 'null' && id !== 'new';
  return useQuery({
    queryKey: [...KEY, 'one', id],
    enabled: valid,
    queryFn: () => invoiceRequest('get', `/purchases/${id}`)
  });
}

function invalidate(qc) {
  qc.invalidateQueries({ queryKey: KEY });
  // ترحيل/إلغاء فاتورة يغيّر ذمة المورد ورصيد القاصة.
  qc.invalidateQueries({ queryKey: ['suppliers'] });
  qc.invalidateQueries({ queryKey: ['cashboxes'] });
  qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
  qc.invalidateQueries({ queryKey: ['inventory'] });
}

export function useCreatePurchase() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => invoiceRequest('post', '/purchases', body),
    successMessage: 'تم حفظ الفاتورة كمسوّدة',
    onSuccess: () => invalidate(qc)
  });
}

export function useUpdatePurchase() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, body }) => invoiceRequest('patch', `/purchases/${id}`, body),
    successMessage: 'تم حفظ التعديلات',
    onSuccess: () => invalidate(qc)
  });
}

export function useDeletePurchase() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id) => invoiceRequest('delete', `/purchases/${id}`),
    successMessage: 'تم حذف الفاتورة',
    onSuccess: () => invalidate(qc)
  });
}

// «حفظ وترحيل» بنداء واحد: لا تبقى الفاتورة مسوّدة إن انكسر نداء الترحيل.
export function useCreateAndPostPurchase() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => invoiceRequest('post', '/purchases/save-and-post', body),
    successMessage: 'تم حفظ الفاتورة وترحيلها وزيادة المخزون',
    onSuccess: () => invalidate(qc)
  });
}

export function usePostPurchase() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, body }) => invoiceRequest('post', `/purchases/${id}/post`, body || {}),
    successMessage: 'تم ترحيل الفاتورة وزيادة المخزون',
    onSuccess: () => invalidate(qc)
  });
}

export function useCancelPurchase() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (arg) => {
      const id = typeof arg === 'string' ? arg : arg.id;
      const reason = typeof arg === 'string' ? '' : (arg.reason || '');
      return invoiceRequest('post', `/purchases/${id}/cancel`, { reason });
    },
    successMessage: 'تم إلغاء الفاتورة وعكس التزاماتها',
    onSuccess: () => {
      invalidate(qc);
      // الإلغاء يغيّر ذمة المورد ورصيده الدائن.
      qc.invalidateQueries({ queryKey: ['suppliers'] });
    }
  });
}
